import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Select, cn } from "../../shared/ui";
import type { ProjectDetail, ProjectStatus } from "../../shared/types/api";
import { STATUS_LABELS } from "./projectVocabulary";

/** §23's lifecycle in the order it is actually walked, so the list reads as a progression. */
const STATUS_ORDER: ProjectStatus[] = [
  "DRAFT",
  "PLANNING",
  "PENDING_APPROVAL",
  "APPROVED",
  "ACTIVE",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
  "ARCHIVED",
];

const CONSEQUENCES: Record<ProjectStatus, string> = {
  DRAFT: "Being shaped. Nothing here is committed yet.",
  PLANNING: "Scope and estimates are being worked out.",
  PENDING_APPROVAL: "Waiting on a decision before delivery can start.",
  APPROVED: "Agreed, and ready for delivery to begin.",
  ACTIVE: "Delivery is under way; work can be logged against it.",
  ON_HOLD: "Paused. It still holds its commitment and stays on reports.",
  COMPLETED: "Delivered. Kept in full for reporting.",
  CANCELLED: "Stopped before delivery. Kept in full for reporting.",
  ARCHIVED: "Closed and out of day-to-day views, but never deleted.",
};

/**
 * Changes the project status (§23).
 *
 * <p>Every state names its consequence, because "on hold" and "cancelled" mean quite different
 * things to capacity planning and to a portfolio report, and a bare dropdown says neither.
 *
 * <p>Nothing is deleted at any state: a project a report covered last year has to remain nameable.
 */
export function StatusDrawer({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<ProjectStatus>("DRAFT");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setStatus(project.status);
      setError(null);
    }
  }, [project]);

  const submit = async () => {
    if (!project) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${project.id}/status`, { status });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the status.");
    } finally {
      setSaving(false);
    }
  };

  // The server refuses this and says why; saying it here first saves a round trip that only
  // ever ends in a rejection.
  const activeWithoutBaseline = status === "ACTIVE" && project?.forecast.hasBaseline === false;

  return (
    <Drawer
      open={Boolean(project)}
      onClose={onClose}
      title="Change status"
      subtitle={project?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={status === project?.status || activeWithoutBaseline}
          >
            {saving ? "Saving…" : "Change status"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="flex items-center gap-2 text-sm">
          <span className="text-ink-500">Currently</span>
          <span className="font-medium text-ink-900">
            {project ? STATUS_LABELS[project.status] : "—"}
          </span>
        </div>

        <Field label="New status">
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as ProjectStatus)}
          >
            {STATUS_ORDER.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <p
          className={cn(
            "rounded-lg border px-3 py-2.5 text-sm",
            status === project?.status
              ? "border-ink-200 bg-ink-50 text-ink-500"
              : "border-brand-200 bg-brand-50 text-brand-700",
          )}
        >
          {CONSEQUENCES[status]}
        </p>

        {activeWithoutBaseline && (
          <InlineAlert tone="warning">
            No baseline has been approved yet. A project cannot become active without one —
            otherwise every variance figure afterwards has nothing to measure against. Approve a
            baseline first, on the Baselines tab.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
